import { Socket } from "socket.io-client";
import { ReorderStreamsParameters, ReorderStreamsType, ConnectRecvTransportType,
   ConnectRecvTransportParameters, CreateWebRTCTransportResponse } from "../@types/types";
import { Device, DtlsParameters  } from 'mediasoup-client/lib/types';

export interface SignalNewConsumerTransportParameters extends ReorderStreamsParameters, ConnectRecvTransportParameters {
  device: Device | null;
  consumingTransports: string[];
  lock_screen: boolean;
  updateConsumingTransports: (transports: string[]) => void;
  connectRecvTransport: ConnectRecvTransportType;
  reorderStreams: ReorderStreamsType;
  getUpdatedAllParams: () => SignalNewConsumerTransportParameters;
  [key: string]: any;
}

export interface SignalNewConsumerTransportOptions {
  remoteProducerId: string;
  islevel: string;
  nsock: Socket;
  parameters: SignalNewConsumerTransportParameters;
}

// Export the type definition for the function
export type SignalNewConsumerTransportType = (options: SignalNewConsumerTransportOptions) => Promise<string[] | void>;

// The server bounds consumer transport creation at seven seconds. Ten seconds
// leaves room for the acknowledgement to cross the network while releasing
// the producer well before joinConsumeRoom's 30-second discovery retry.
export const CONSUMER_TRANSPORT_ACK_TIMEOUT_MS = 10_000;

/**
 * Signals the creation of a new consumer transport.
 * 
 * @param {SignalNewConsumerTransportOptions} options - The options for signaling a new consumer transport.
 * @param {string} options.remoteProducerId - The ID of the remote producer.
 * @param {string} options.islevel - Indicates the level of the consumer.
 * @param {Socket} options.nsock - The socket instance for communication.
 * @param {SignalNewConsumerTransportParameters} options.parameters - The parameters for the transport.
 * 
 * @returns {Promise<string[] | void>} A promise that resolves to an array of consuming transports or void.
 * 
 * @throws Will throw an error if the signaling process fails.
 * 
 * @example
 * const options = {
 *   remoteProducerId: 'producer-id',
 *   islevel: '1',
 *   nsock: socketInstance,
 *   parameters: {
 *     device: mediaDevice,
 *     consumingTransports: [],
 *     lock_screen: false,
 *     updateConsumingTransports: updateFunction,
 *     connectRecvTransport: connectFunction,
 *     reorderStreams: reorderFunction,
 *     getUpdatedAllParams: getUpdatedParamsFunction,
 *   },
 * };
 * 
 * signalNewConsumerTransport(options)
 *   .then(consumingTransports => {
 *     console.log('Consuming Transports:', consumingTransports);
 *   })
 *   .catch(error => {
 *     console.error('Error signaling new consumer transport:', error);
 *   });
 */

export const signalNewConsumerTransport = async ({
  remoteProducerId,
  islevel,
  nsock,
  parameters,
}: SignalNewConsumerTransportOptions): Promise<string[] | void> => {
  try {
    let {
      device,
      consumingTransports,
      lock_screen,
      updateConsumingTransports,
      connectRecvTransport,
      reorderStreams,
    } = parameters;

    // Get updated parameters
    const updatedParams = parameters.getUpdatedAllParams();
    device = updatedParams.device;
    consumingTransports = updatedParams.consumingTransports;

    // A consuming socket may announce an existing producer while the room is
    // still creating its mediasoup Device. Do not reserve that producer until
    // the device exists: the post-join producer sweep will retry it. Reserving
    // first poisoned the de-duplication list, so the retry was skipped forever.
    if (!device) {
      return consumingTransports;
    }

    // Check if already consuming
    if (consumingTransports.includes(remoteProducerId)) {
      return consumingTransports;
    }

    // Reserve immutably so callers holding the previous snapshot do not see a
    // half-created consumer. Any transport-creation failure releases it.
    const reservedTransports = [...consumingTransports, remoteProducerId];
    updateConsumingTransports(reservedTransports);

    const releaseReservation = () => {
      const latest = parameters.getUpdatedAllParams();
      const current = latest.consumingTransports || [];
      if (current.includes(remoteProducerId)) {
        latest.updateConsumingTransports(
          current.filter((producerId) => producerId !== remoteProducerId)
        );
      }
    };

    let acknowledgementExpired = false;
    const acknowledgementTimer = setTimeout(() => {
      acknowledgementExpired = true;
      releaseReservation();
    }, CONSUMER_TRANSPORT_ACK_TIMEOUT_MS);

    // Emit createWebRtcTransport event to signal a new consumer
    try {
      nsock.emit(
        "createWebRtcTransport",
        { consumer: true, islevel },
        async (response: {
          params?: CreateWebRTCTransportResponse;
          error?: string;
        }) => {
          clearTimeout(acknowledgementTimer);

          const params = response?.params;

          if (acknowledgementExpired) {
            const latest = parameters.getUpdatedAllParams();
            const current = latest.consumingTransports || [];
            // A newer discovery attempt owns this producer now. Ignore the
            // stale acknowledgement instead of creating a duplicate consumer.
            if (current.includes(remoteProducerId)) return;

            // The reservation has already been released. A late error needs no
            // cleanup and a malformed acknowledgement cannot create a transport.
            if (response?.error || !params || params.error) return;

            // The retry has not started yet, so reclaim the lease and finish
            // this valid late acknowledgement without waiting another 30s.
            latest.updateConsumingTransports([...current, remoteProducerId]);
          }

          // The consuming server returns failures as a top-level `error`, while
          // older handlers used `params.error`. Accept both response shapes so
          // an acknowledged server failure cannot strand the reservation.
          if (response?.error || !params || params.error) {
            releaseReservation();
            return;
          }

          try {
            // Create a new receiving transport using the received parameters
            const consumerTransport = device.createRecvTransport({ ...params });

            // Handle 'connect' event for the consumer transport
            consumerTransport.on(
              "connect",
              async (
                { dtlsParameters }: { dtlsParameters: DtlsParameters },
                callback: () => void,
                errback: (error: any) => void
              ) => {
                try {
                  // Emit transport-recv-connect event to signal connection
                  nsock.emit("transport-recv-connect", {
                    dtlsParameters,
                    serverConsumerTransportId: params.id,
                  });
                  callback();
                } catch (error) {
                  errback(error);
                }
              }
            );

            // Listen for connection state change
            consumerTransport.on("connectionstatechange", async (state: string) => {
              switch (state) {
                case "connecting":
                  // Handle connecting state
                  break;

                case "connected":
                  // Handle connected state
                  break;

                case "failed":
                  // Handle failed state
                  consumerTransport.close();
                  releaseReservation();

                  // Reorder streams based on conditions
                  if (lock_screen) {
                    await reorderStreams({ add: true, parameters });
                  } else {
                    await reorderStreams({ add: false, parameters });
                  }
                  break;

                default:
                  break;
              }
            });

            // Connect the receiving transport
            await connectRecvTransport({
              consumerTransport,
              remoteProducerId,
              serverConsumerTransportId: params.id,
              nsock,
              parameters,
            });
          } catch (error) {
            console.log(error, "createRecvTransport error");
            releaseReservation();
            // Handle error
            return;
          }
        }
      );
    } catch (error) {
      clearTimeout(acknowledgementTimer);
      releaseReservation();
      throw error;
    }

    return reservedTransports;
  } catch (error) {
    console.log(error, "signalNewConsumerTransport error");
    // Handle error
    return;
  }
};

